import { randomUUID } from "node:crypto";
import type {
  ChatMessage,
  ModelEvent,
  ModelInfo,
  ModelProvider,
  ModelRequest,
  ProviderContext,
} from "@mewbleh/nori-plugin-sdk";
import { readResponseJson, readResponseText } from "./http.js";

function textContent(message: ChatMessage): string | Array<Record<string, unknown>> {
  if (typeof message.content === "string") return message.content;
  return message.content.map((part) => {
    if (part.type === "text") return { type: "text", text: part.text };
    if (part.type === "image") {
      const url = part.url ?? `data:${part.mediaType};base64,${part.data ?? ""}`;
      return { type: "image_url", image_url: { url } };
    }
    return { type: "text", text: `[File: ${part.name ?? "attachment"} (${part.mediaType})]` };
  });
}

async function responseError(response: Response): Promise<Error> {
  const body = await readResponseText(response, 64 * 1024, "Provider error response").catch(() => "response body is too large");
  const error = new Error(`Provider request failed (${response.status}): ${body.slice(0, 500)}`);
  Object.assign(error, { status: response.status, retryable: response.status === 429 || response.status >= 500 });
  return error;
}

async function* parseSse(response: Response, signal: AbortSignal): AsyncGenerator<{ event?: string; data: string }> {
  if (!response.body) throw new Error("Provider returned an empty streaming body");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let totalBytes = 0;
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      totalBytes += value.byteLength;
      if (totalBytes > 16 * 1024 * 1024) throw new Error("Provider streaming response exceeds 16 MiB");
      buffer += decoder.decode(value, { stream: true }).replaceAll("\r\n", "\n");
      let boundary = buffer.indexOf("\n\n");
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        let event: string | undefined;
        const data: string[] = [];
        for (const line of block.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
        }
        if (data.length) yield event ? { event, data: data.join("\n") } : { data: data.join("\n") };
        boundary = buffer.indexOf("\n\n");
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) {
      let event: string | undefined;
      const data: string[] = [];
      for (const line of buffer.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if (data.length) yield event ? { event, data: data.join("\n") } : { data: data.join("\n") };
    }
  } finally {
    reader.releaseLock();
  }
}

type OpenAiProviderOptions = { id: string; baseUrl: string; headers?: Record<string, string> };

function responsesContent(message: ChatMessage): Array<Record<string, unknown>> | string {
  if (typeof message.content === "string") return message.content;
  return message.content.map((part) => {
    if (part.type === "text") return { type: message.role === "assistant" ? "output_text" : "input_text", text: part.text };
    if (part.type === "image") return { type: "input_image", image_url: part.url ?? `data:${part.mediaType};base64,${part.data ?? ""}` };
    return { type: "input_text", text: `[File: ${part.name ?? "attachment"} (${part.mediaType})]` };
  });
}

function responsesInput(messages: ChatMessage[]): Array<Record<string, unknown>> {
  const input: Array<Record<string, unknown>> = [];
  for (const message of messages) {
    if (message.role === "system") continue;
    if (message.role === "tool") {
      input.push({ type: "function_call_output", call_id: message.toolCallId, output: typeof message.content === "string" ? message.content : JSON.stringify(message.content) });
      continue;
    }
    const content = responsesContent(message);
    if ((typeof content === "string" && content) || (Array.isArray(content) && content.length)) input.push({ role: message.role, content });
    for (const call of message.toolCalls ?? []) input.push({ type: "function_call", call_id: call.id, name: call.name, arguments: call.arguments });
  }
  return input;
}

export class OpenAiCompatibleProvider implements ModelProvider {
  readonly id: string;
  readonly baseUrl: string;
  readonly extraHeaders: Record<string, string>;

  constructor(options: OpenAiProviderOptions) {
    this.id = options.id;
    this.baseUrl = options.baseUrl.replace(/\/$/u, "");
    this.extraHeaders = options.headers ?? {};
  }

  #headers(context: Omit<ProviderContext, "signal">): Record<string, string> {
    return {
      "content-type": "application/json",
      ...(context.apiKey ? { authorization: `Bearer ${context.apiKey}` } : {}),
      ...this.extraHeaders,
    };
  }

  async listModels(context: Omit<ProviderContext, "signal">): Promise<ModelInfo[]> {
    if (context.auth?.type === "oauth") {
      return (context.configuredModels ?? []).map((id) => ({ id, supportsTools: true, supportsImages: true }));
    }
    const response = await context.fetch(`${context.baseUrl ?? this.baseUrl}/models`, { headers: this.#headers(context) });
    if (!response.ok) throw await responseError(response);
    const body = await readResponseJson<{ data?: Array<{ id: string }> }>(response, 4 * 1024 * 1024, "Provider model list");
    return (body.data ?? []).map((model) => ({ id: model.id }));
  }

  async *stream(request: ModelRequest, context: ProviderContext): AsyncIterable<ModelEvent> {
    if (context.auth?.type === "oauth") {
      yield* this.#streamCodexResponses(request, context);
      return;
    }
    const body: Record<string, unknown> = {
      model: request.model,
      stream: true,
      stream_options: { include_usage: true },
      messages: request.messages.map((message) => ({
        role: message.role,
        content: textContent(message),
        ...(message.name ? { name: message.name } : {}),
        ...(message.toolCallId ? { tool_call_id: message.toolCallId } : {}),
        ...(message.toolCalls?.length
          ? {
              tool_calls: message.toolCalls.map((call) => ({
                id: call.id,
                type: "function",
                function: { name: call.name, arguments: call.arguments },
              })),
            }
          : {}),
      })),
    };
    if (request.tools?.length) {
      body.tools = request.tools.map((tool) => ({
        type: "function",
        function: { name: tool.name, description: tool.description, parameters: tool.inputSchema },
      }));
    }
    if (request.temperature !== undefined) body.temperature = request.temperature;
    if (request.maxTokens !== undefined) body.max_tokens = request.maxTokens;
    const response = await context.fetch(`${context.baseUrl ?? this.baseUrl}/chat/completions`, {
      method: "POST",
      headers: this.#headers(context),
      body: JSON.stringify(body),
      signal: context.signal,
    });
    if (!response.ok) throw await responseError(response);
    const toolCalls = new Map<number, { id: string; name: string; arguments: string }>();
    for await (const event of parseSse(response, context.signal)) {
      if (event.data === "[DONE]") break;
      const chunk = JSON.parse(event.data) as {
        choices?: Array<{
          delta?: { content?: string; tool_calls?: Array<{ index: number; id?: string; function?: { name?: string; arguments?: string } }> };
          finish_reason?: string;
        }>;
        usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
      };
      const choice = chunk.choices?.[0];
      if (choice?.delta?.content) yield { type: "text-delta", text: choice.delta.content };
      for (const call of choice?.delta?.tool_calls ?? []) {
        const current = toolCalls.get(call.index) ?? { id: call.id ?? `call_${call.index}`, name: "", arguments: "" };
        if (call.id) current.id = call.id;
        if (call.function?.name) current.name += call.function.name;
        if (call.function?.arguments) current.arguments += call.function.arguments;
        toolCalls.set(call.index, current);
      }
      if (chunk.usage) {
        yield {
          type: "usage",
          usage: {
            inputTokens: chunk.usage.prompt_tokens ?? 0,
            outputTokens: chunk.usage.completion_tokens ?? 0,
            totalTokens: chunk.usage.total_tokens ?? 0,
          },
        };
      }
      if (choice?.finish_reason) {
        for (const call of toolCalls.values()) yield { type: "tool-call", ...call };
        yield { type: "finish", reason: choice.finish_reason === "tool_calls" ? "tool" : choice.finish_reason === "length" ? "length" : "stop" };
      }
    }
  }

  async *#streamCodexResponses(request: ModelRequest, context: ProviderContext): AsyncIterable<ModelEvent> {
    const auth = context.auth;
    if (auth?.type !== "oauth") throw new Error("OpenAI OAuth context is required");
    const system = request.messages
      .filter((message) => message.role === "system")
      .map((message) => typeof message.content === "string" ? message.content : JSON.stringify(message.content))
      .join("\n\n") || "You are a helpful assistant.";
    const body: Record<string, unknown> = {
      model: request.model,
      store: false,
      stream: true,
      instructions: system,
      input: responsesInput(request.messages),
      text: { verbosity: "low" },
      include: ["reasoning.encrypted_content"],
      ...(request.tools?.length ? {
        tool_choice: "auto",
        parallel_tool_calls: true,
        tools: request.tools.map((tool) => ({ type: "function", name: tool.name, description: tool.description, parameters: tool.inputSchema, strict: false })),
      } : {}),
      ...(request.temperature !== undefined ? { temperature: request.temperature } : {}),
      ...(request.maxTokens !== undefined ? { max_output_tokens: request.maxTokens } : {}),
    };
    const baseUrl = (context.oauthBaseUrl ?? "https://chatgpt.com/backend-api").replace(/\/+$/u, "");
    const endpoint = baseUrl.endsWith("/codex/responses") ? baseUrl : baseUrl.endsWith("/codex") ? `${baseUrl}/responses` : `${baseUrl}/codex/responses`;
    const requestId = randomUUID();
    const response = await context.fetch(endpoint, {
      method: "POST",
      headers: {
        authorization: `Bearer ${auth.accessToken}`,
        "chatgpt-account-id": auth.accountId,
        originator: "nori",
        "user-agent": "nori/0.3.0",
        "openai-beta": "responses=experimental",
        accept: "text/event-stream",
        "content-type": "application/json",
        "x-client-request-id": requestId,
        session_id: requestId,
      },
      body: JSON.stringify(body),
      signal: context.signal,
    });
    if (!response.ok) throw await responseError(response);
    const calls = new Map<string, { id: string; name: string; arguments: string; emitted: boolean }>();
    let sawTool = false;
    for await (const envelope of parseSse(response, context.signal)) {
      if (envelope.data === "[DONE]") break;
      const event = JSON.parse(envelope.data) as Record<string, any>;
      const type = String(event.type ?? envelope.event ?? "");
      if (type === "error" || type === "response.failed") {
        const detail = event.error ?? event.response?.error ?? {};
        throw Object.assign(new Error(String(detail.message ?? detail.code ?? "OpenAI Codex response failed")), { status: response.status, retryable: false });
      }
      if (type === "response.output_text.delta" && typeof event.delta === "string") yield { type: "text-delta", text: event.delta };
      if (type === "response.output_item.added" && event.item?.type === "function_call") {
        const key = String(event.item.id ?? event.item.call_id ?? event.output_index ?? calls.size);
        calls.set(key, { id: String(event.item.call_id ?? event.item.id ?? `call_${calls.size}`), name: String(event.item.name ?? ""), arguments: String(event.item.arguments ?? ""), emitted: false });
      }
      if (type === "response.function_call_arguments.delta") {
        const key = String(event.item_id ?? event.call_id ?? event.output_index ?? "");
        const call = calls.get(key) ?? [...calls.values()].at(-1);
        if (call) call.arguments += String(event.delta ?? "");
      }
      if (type === "response.function_call_arguments.done") {
        const key = String(event.item_id ?? event.call_id ?? event.output_index ?? "");
        const call = calls.get(key) ?? [...calls.values()].at(-1);
        if (call && typeof event.arguments === "string") call.arguments = event.arguments;
      }
      if (type === "response.output_item.done" && event.item?.type === "function_call") {
        const key = String(event.item.id ?? event.item.call_id ?? event.output_index ?? "");
        const call = calls.get(key) ?? [...calls.values()].at(-1);
        if (call && !call.emitted) {
          if (typeof event.item.arguments === "string") call.arguments = event.item.arguments;
          call.emitted = true;
          sawTool = true;
          yield { type: "tool-call", id: call.id, name: call.name, arguments: call.arguments };
        }
      }
      if (type === "response.completed" || type === "response.done" || type === "response.incomplete") {
        for (const call of calls.values()) {
          if (!call.emitted) {
            call.emitted = true;
            sawTool = true;
            yield { type: "tool-call", id: call.id, name: call.name, arguments: call.arguments };
          }
        }
        const usage = event.response?.usage;
        if (usage) yield { type: "usage", usage: { inputTokens: Number(usage.input_tokens ?? 0), outputTokens: Number(usage.output_tokens ?? 0), totalTokens: Number(usage.total_tokens ?? Number(usage.input_tokens ?? 0) + Number(usage.output_tokens ?? 0)) } };
        const incompleteReason = String(event.response?.incomplete_details?.reason ?? "");
        yield { type: "finish", reason: sawTool ? "tool" : incompleteReason === "max_output_tokens" ? "length" : "stop" };
        return;
      }
    }
    for (const call of calls.values()) if (!call.emitted) { sawTool = true; yield { type: "tool-call", id: call.id, name: call.name, arguments: call.arguments }; }
    yield { type: "finish", reason: sawTool ? "tool" : "stop" };
  }
}

export class AnthropicProvider implements ModelProvider {
  readonly id = "anthropic";
  readonly baseUrl = "https://api.anthropic.com/v1";

  async listModels(context: Omit<ProviderContext, "signal">): Promise<ModelInfo[]> {
    const response = await context.fetch(`${context.baseUrl ?? this.baseUrl}/models`, {
      headers: { "x-api-key": context.apiKey ?? "", "anthropic-version": "2023-06-01" },
    });
    if (!response.ok) throw await responseError(response);
    const body = await readResponseJson<{ data?: Array<{ id: string; display_name?: string }> }>(response, 4 * 1024 * 1024, "Anthropic model list");
    return (body.data ?? []).map((model) => ({ id: model.id, ...(model.display_name ? { displayName: model.display_name } : {}) }));
  }

  async *stream(request: ModelRequest, context: ProviderContext): AsyncIterable<ModelEvent> {
    const system = request.messages.filter((message) => message.role === "system").map((message) => textContent(message)).join("\n\n");
    const messages = request.messages
      .filter((message) => message.role !== "system")
      .map((message) => {
        if (message.role === "tool") {
          return {
            role: "user",
            content: [
              {
                type: "tool_result",
                tool_use_id: message.toolCallId,
                content: typeof message.content === "string" ? message.content : JSON.stringify(message.content),
              },
            ],
          };
        }
        if (message.role === "assistant" && message.toolCalls?.length) {
          const content: Array<Record<string, unknown>> = [];
          const text = textContent(message);
          if (typeof text === "string" && text) content.push({ type: "text", text });
          for (const call of message.toolCalls) {
            let input: unknown = {};
            try { input = JSON.parse(call.arguments || "{}"); } catch { input = { raw: call.arguments }; }
            content.push({ type: "tool_use", id: call.id, name: call.name, input });
          }
          return { role: "assistant", content };
        }
        return { role: message.role === "assistant" ? "assistant" : "user", content: textContent(message) };
      });
    const response = await context.fetch(`${context.baseUrl ?? this.baseUrl}/messages`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-api-key": context.apiKey ?? "",
        "anthropic-version": "2023-06-01",
      },
      body: JSON.stringify({
        model: request.model,
        max_tokens: request.maxTokens ?? 4096,
        stream: true,
        ...(system ? { system } : {}),
        messages,
        ...(request.tools?.length
          ? { tools: request.tools.map((tool) => ({ name: tool.name, description: tool.description, input_schema: tool.inputSchema })) }
          : {}),
      }),
      signal: context.signal,
    });
    if (!response.ok) throw await responseError(response);
    const toolCalls = new Map<number, { id: string; name: string; arguments: string }>();
    for await (const event of parseSse(response, context.signal)) {
      const data = JSON.parse(event.data) as Record<string, any>;
      if (event.event === "content_block_start" && data.content_block?.type === "tool_use") {
        toolCalls.set(Number(data.index), { id: String(data.content_block.id), name: String(data.content_block.name), arguments: "" });
      } else if (event.event === "content_block_delta" && data.delta?.type === "text_delta") {
        yield { type: "text-delta", text: String(data.delta.text) };
      } else if (event.event === "content_block_delta" && data.delta?.type === "input_json_delta") {
        const call = toolCalls.get(Number(data.index));
        if (call) call.arguments += String(data.delta.partial_json ?? "");
      } else if (event.event === "message_delta") {
        const usage = data.usage as { output_tokens?: number } | undefined;
        if (usage) yield { type: "usage", usage: { inputTokens: 0, outputTokens: usage.output_tokens ?? 0, totalTokens: usage.output_tokens ?? 0 } };
      } else if (event.event === "message_stop") {
        for (const call of toolCalls.values()) yield { type: "tool-call", ...call };
        yield { type: "finish", reason: toolCalls.size ? "tool" : "stop" };
      }
    }
  }
}

export class OllamaProvider implements ModelProvider {
  readonly id = "ollama";
  readonly baseUrl = "http://127.0.0.1:11434";

  async listModels(context: Omit<ProviderContext, "signal">): Promise<ModelInfo[]> {
    const response = await context.fetch(`${context.baseUrl ?? this.baseUrl}/api/tags`);
    if (!response.ok) throw await responseError(response);
    const body = await readResponseJson<{ models?: Array<{ name: string }> }>(response, 4 * 1024 * 1024, "Ollama model list");
    return (body.models ?? []).map((model) => ({ id: model.name }));
  }

  async *stream(request: ModelRequest, context: ProviderContext): AsyncIterable<ModelEvent> {
    const response = await context.fetch(`${context.baseUrl ?? this.baseUrl}/api/chat`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        model: request.model,
        stream: true,
        messages: request.messages.map((message) => ({
          role: message.role,
          content: typeof message.content === "string" ? message.content : JSON.stringify(message.content),
          ...(message.toolCalls?.length
            ? {
                tool_calls: message.toolCalls.map((call) => ({
                  function: { name: call.name, arguments: JSON.parse(call.arguments || "{}") },
                })),
              }
            : {}),
        })),
        ...(request.tools?.length ? { tools: request.tools.map((tool) => ({ type: "function", function: { name: tool.name, description: tool.description, parameters: tool.inputSchema } })) } : {}),
      }),
      signal: context.signal,
    });
    if (!response.ok) throw await responseError(response);
    if (!response.body) throw new Error("Ollama returned an empty body");
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = "";
    let totalBytes = 0;
    try {
      while (!context.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        totalBytes += Buffer.byteLength(value);
        if (totalBytes > 16 * 1024 * 1024) throw new Error("Ollama streaming response exceeds 16 MiB");
        buffer += value;
        if (Buffer.byteLength(buffer) > 1024 * 1024) throw new Error("Ollama emitted an oversized NDJSON frame");
        let newline = buffer.indexOf("\n");
        while (newline >= 0) {
          const line = buffer.slice(0, newline).trim();
          buffer = buffer.slice(newline + 1);
          if (line) {
            const chunk = JSON.parse(line) as Record<string, any>;
            if (chunk.message?.content) yield { type: "text-delta", text: String(chunk.message.content) };
            for (const [index, call] of (chunk.message?.tool_calls ?? []).entries()) {
              yield { type: "tool-call", id: `ollama_${index}`, name: String(call.function?.name ?? ""), arguments: JSON.stringify(call.function?.arguments ?? {}) };
            }
            if (chunk.done) {
              const input = Number(chunk.prompt_eval_count ?? 0);
              const output = Number(chunk.eval_count ?? 0);
              yield { type: "usage", usage: { inputTokens: input, outputTokens: output, totalTokens: input + output } };
              yield { type: "finish", reason: (chunk.message?.tool_calls?.length ?? 0) > 0 ? "tool" : "stop" };
            }
          }
          newline = buffer.indexOf("\n");
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

export function builtInProviders(): ModelProvider[] {
  return [
    new OpenAiCompatibleProvider({ id: "openai", baseUrl: "https://api.openai.com/v1" }),
    new AnthropicProvider(),
    new OpenAiCompatibleProvider({
      id: "openrouter",
      baseUrl: "https://openrouter.ai/api/v1",
      headers: { "HTTP-Referer": "https://github.com/mewbleh/nori", "X-Title": "Nori" },
    }),
    new OllamaProvider(),
    new OpenAiCompatibleProvider({ id: "cerebras", baseUrl: "https://api.cerebras.ai/v1" }),
    new OpenAiCompatibleProvider({ id: "deepinfra", baseUrl: "https://api.deepinfra.com/v1/openai" }),
    new OpenAiCompatibleProvider({ id: "deepseek", baseUrl: "https://api.deepseek.com" }),
    new OpenAiCompatibleProvider({ id: "featherless", baseUrl: "https://api.featherless.ai/v1" }),
    new OpenAiCompatibleProvider({ id: "fireworks", baseUrl: "https://api.fireworks.ai/inference/v1" }),
    new OpenAiCompatibleProvider({ id: "groq", baseUrl: "https://api.groq.com/openai/v1" }),
    new OpenAiCompatibleProvider({ id: "huggingface", baseUrl: "https://router.huggingface.co/v1" }),
    new OpenAiCompatibleProvider({ id: "lmstudio", baseUrl: "http://127.0.0.1:1234/v1" }),
    new OpenAiCompatibleProvider({ id: "mistral", baseUrl: "https://api.mistral.ai/v1" }),
    new OpenAiCompatibleProvider({ id: "nvidia", baseUrl: "https://integrate.api.nvidia.com/v1" }),
    new OpenAiCompatibleProvider({ id: "together", baseUrl: "https://api.together.xyz/v1" }),
    new OpenAiCompatibleProvider({ id: "venice", baseUrl: "https://api.venice.ai/api/v1" }),
    new OpenAiCompatibleProvider({ id: "xai", baseUrl: "https://api.x.ai/v1" }),
    new OpenAiCompatibleProvider({ id: "vllm", baseUrl: "http://127.0.0.1:8000/v1" }),
  ];
}
